import clsx from "clsx";
import { useState } from "react";
import { showErrorAlert } from "../../../../../../helpers/alert";

const Selector = ({ title, subtitle, active, purchased, onClick }: { title: string, subtitle: string, active: boolean, purchased: boolean, onClick: () => void }) => 
    <div 
        className={clsx("message-experience-service-selector", { 'active': active || purchased, 'disabled': purchased })} 
        onClick={purchased ? undefined : onClick}
        style={{ cursor: purchased ? 'not-allowed' : 'pointer', opacity: purchased ? 0.7 : 1, width: '100%' }}
    >
        {purchased || active ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                <rect width="18" height="18" rx="2" fill={purchased ? "#808080" : "#460107"}/>
                <path d="M14.3333 6L7.91667 12.4167L5 9.5" stroke="white" stroke-width="1.5"/>
            </svg>
        ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                <rect x="1" y="1" width="16" height="16" rx="1" stroke="#DFDFDF" stroke-width="2"/>
            </svg>
        )}
        <div className="d-flex flex-column">
            <span className="service-selector-title">{title}</span>
            <span className="service-selector-subtitle">
                {subtitle} {purchased && <span style={{ color: '#808080', fontWeight: 'normal', marginLeft: '5px' }}>(Purchased)</span>}
            </span>
        </div>
    </div>

type Props = {
    experience?: any;
}

export const MessagesExperienceServices = ({ experience }: Props) => {
    const [open, setOpen] = useState(true);
    const enhancements = experience?.enhancements || [];
    const [activeIds, setActiveIds] = useState<number[]>([]);

    const handleSelector = (id: number) => {
        setActiveIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    }

    const unpurchasedEnhancements = enhancements.filter((e: any) => !e.purchased);
    const hasEnhancements = enhancements.length > 0;
    const showBuyButton = hasEnhancements && unpurchasedEnhancements.length > 0;
    
    // We only enable the button if the user has selected at least one unpurchased enhancement.
    const selectedUnpurchasedIds = activeIds.filter(id => {
        const enh = enhancements.find((e: any) => e.id === id);
        return enh && !enh.purchased;
    });
    const isButtonDisabled = selectedUnpurchasedIds.length === 0;

    const handleBuy = () => {
        const payload = {
            items: selectedUnpurchasedIds,
            user_id: experience?.userId || experience?.user_id || null,
            experience_id: experience?.experienceId || experience?.experience_id || null,
            experience_date_id: experience?.experienceDateId || experience?.experience_date_id || null
        };
        console.log("Purchase payload prepared:", payload);
        showErrorAlert({ 
            title: 'coming soon', 
            message: `` 
            // message: `Purchase Payload: ${JSON.stringify(payload)}` 
        });
    };

    return (
        <div className={clsx("messages-info-card", { "collapsed": !open })}>
            <div className="d-flex flex-row justify-content-between w-100" style={{ cursor: 'pointer' }} onClick={() => setOpen(prev => !prev)}>
                <span className="title">Optional Services</span>
                <div className="collapse-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                        <path d="M12.5194 9.70701C12.4759 9.75059 12.4242 9.78517 12.3673 9.80876C12.3104 9.83235 12.2494 9.84449 12.1878 9.84449C12.1262 9.84449 12.0652 9.83235 12.0083 9.80876C11.9514 9.78517 11.8997 9.75059 11.8561 9.70701L7.50026 5.35056L3.1444 9.70701C3.05644 9.79497 2.93715 9.84438 2.81276 9.84438C2.68837 9.84438 2.56908 9.79497 2.48112 9.70701C2.39316 9.61905 2.34375 9.49976 2.34375 9.37537C2.34375 9.25098 2.39316 9.13168 2.48112 9.04373L7.16862 4.35623C7.21215 4.31265 7.26385 4.27807 7.32076 4.25448C7.37766 4.23089 7.43866 4.21875 7.50026 4.21875C7.56186 4.21875 7.62286 4.23089 7.67976 4.25448C7.73667 4.27807 7.78837 4.31265 7.8319 4.35623L12.5194 9.04373C12.563 9.08726 12.5976 9.13896 12.6211 9.19587C12.6447 9.25277 12.6569 9.31377 12.6569 9.37537C12.6569 9.43697 12.6447 9.49797 12.6211 9.55487C12.5976 9.61178 12.563 9.66348 12.5194 9.70701Z" fill="#151515"/>
                    </svg>
                </div>
            </div>
            {open && (
                <div className="d-flex flex-column w-100 align-items-center" style={{ gap: '20px' }}>
                    {!hasEnhancements ? (
                        <span className="text-muted text-center" style={{ fontSize: '12px', padding: '10px 0' }}>
                            This experience does not have optional services.
                        </span>
                    ) : (
                        <div className="d-flex flex-column w-100 align-items-center" style={{ gap: '10px' }}>
                            {enhancements.map((s: any, i: number) => (
                                <Selector 
                                    key={i}
                                    title={s.name} 
                                    subtitle={`$${s.price}`} 
                                    active={activeIds.includes(s.id)}
                                    purchased={!!s.purchased}
                                    onClick={() => handleSelector(s.id)} 
                                />
                            ))}
                        </div>
                    )}
                    {showBuyButton && (
                        <button 
                            className="message-btn-primary" 
                            disabled={isButtonDisabled} 
                            style={{ opacity: isButtonDisabled ? 0.5 : 1, cursor: isButtonDisabled ? 'not-allowed' : 'pointer' }}
                            onClick={handleBuy}
                        >
                            <span>Buy services</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="4" viewBox="0 0 14 4" fill="none">
                                <path d="M8.5 0.382812L12 3.38696H0" stroke="white"/>
                            </svg>
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}